import _get from 'lodash/get';
import _merge from 'lodash/merge';
import Store from 'lightningjs-redux';
import { pairing, platformCode } from '@bedrock-rdk/config';
import { sendEvent } from '../../../../logic/analytics/analytics.utils';
import { SubscriptionExpiredBase } from '../base/subscriptionExpiredBase.component';
import { t } from '../../../i18n';
import { translate } from '../../../tornado/pairing/pairing.utils';
import { HIDE_POPUP_SIGNAL } from '../../../popupContainer/popupContainer.constants';
import { makeNotification, NOTIFICATION_LEVEL } from '../../../tornado/notifications';
import { NOTIFICATION_SIGNAL, SELECTED_SIGNAL } from '../../../tornado/signals';
import { createItemAction } from '../../../cta/cta.actions';
import { PairingSelector } from '../../../tornado/pairing/selector/pairingSelector.component';
import { BrandIcon } from '../../../tornado/brand-icon/brandIcon.component';
import { DesktopPairing } from '../../../tornado/pairing/desktop/desktopPairing.component';
import { MobilePairing } from '../../../tornado/pairing/mobile/mobilePairing.component';
import { getInitialStyle } from '../base/subscriptionExpiredBase.style';
import { PAIRING_METHOD } from '../../../pairing/pairing.constants';
import { getPairingMethod } from '../../../pairing/pairing.utils';

export class Upsell extends SubscriptionExpiredBase {
  static _template() {
    return _merge(
      {
        Background: {
          rect: true,
        },
        CustomerIcon: {
          type: BrandIcon,
        },
        Left: {
          type: PairingSelector,
          buttons: [
            {
              label: t('upsell.mobile.button'),
              iconLeft: 'QRCode',
              iconRight: 'ArrowRight',
            },
            {
              label: t('upsell.computer.button'),
              iconLeft: 'Laptop',
              iconRight: 'ArrowRight',
            },
            {
              label: t('global.back'),
            },
          ],
          labels: {
            title: t('upsell.title'),
          },
          signals: {
            [SELECTED_SIGNAL]: '_switchViews',
          },
        },
        Right: {
          Mobile: {
            type: MobilePairing,
            labels: {
              title: t('upsell.mobile.title'),
              steps: 'upsell.mobile.step',
              stepsCount: 3,
            },
            url: pairing.upsellURL,
            signals: {
              [SELECTED_SIGNAL]: '_handleUnlockAttempt',
            },
          },
          Desktop: {
            type: DesktopPairing,
            labels: {
              title: t('upsell.computer.title'),
            },
            steps: [
              t('upsell.computer.step1', { url: pairing.upsellURL }),
              t('upsell.computer.step2'),
              t('upsell.computer.step3'),
            ],
            signals: {
              [SELECTED_SIGNAL]: '_handleUnlockAttempt',
            },
          },
        },
      },
      getInitialStyle(),
    );
  }

  _active() {
    super._active();

    const url = getPairingMethod() === PAIRING_METHOD.SSI ? pairing.ssiURL : pairing.upsellURL;

    const QRCodeUrl = this._getUrlWithParams(url);

    sendEvent({ key: 'upsellMessageViewed' });

    this.patch({
      Right: {
        Mobile: { url: QRCodeUrl },
        Desktop: {
          steps: [t('upsell.computer.step1', { url }), t('upsell.computer.step2'), t('upsell.computer.step3')],
        },
      },
    });
  }

  set isPaired(value) {
    this._isPaired = value;
    const key = value ? 'pairedButton' : 'unpairedButton';

    this.tag('Desktop').button = translate(`upsell.computer.${key}`);
    this.tag('Mobile').button = translate(`upsell.mobile.${key}`);
  }

  _resolveLock() {
    const required = this._requiredFeatures && this._requiredFeatures.length ? this._requiredFeatures : [];
    const available = this._services && this._services.length ? this._services : [];

    const offersMatching =
      !required.length ||
      (available.length
        ? this._services.some(service => required.every(product => service.products.includes(product)))
        : false);

    if (offersMatching) {
      Store.dispatch(createItemAction(this._payload));
      this.signal(HIDE_POPUP_SIGNAL);
    } else {
      this.fireAncestors(`$${NOTIFICATION_SIGNAL}`, makeNotification(NOTIFICATION_LEVEL.ERROR, t('upsell.error')));
    }
  }

  _getUrlWithParams(url) {
    const unlockTarget = _get(this._lock, 'reasonAttributes.unlockTarget', {});
    if (!unlockTarget) {
      return url;
    }

    const additionalParams = _get(unlockTarget, `value_${unlockTarget.type}.details.additionalParams`, {});
    if (!additionalParams || Object.keys(additionalParams).length === 0) {
      return url;
    }

    additionalParams.platformCode = platformCode;
    additionalParams.PartnerContext = this._deviceBrand;

    const urlObj = new URL(url);
    Object.entries(additionalParams).forEach(([key, value]) => {
      urlObj.searchParams.append(key, value);
    });

    url = urlObj.toString();

    return url;
  }

  async _handleUnlockAttempt() {
    sendEvent({ key: 'upsellRegisterClick' });
    super._handleUnlockAttempt();
  }
}
